/*
 * TiersForm.tsx : Un formulaire pour ajouter ou modifier un tiers.
 */

// Import React
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// Import FontAwesome
import {
	faBuilding,
	faCity,
	faIndustry,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	CheckboxField,
	finishFormSave,
	FormSaveActions,
	InputField,
	useFormDirty,
} from '../../../common';
import CardHeader from '../../../layout/CardHeader';

// Import Tiers
import { TiersData } from '../types/types';
import { createTiers, getTiersById, updateTiers } from '../services/tiersApi';

// Composant TiersForm
const TiersForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<TiersData>({
		id: 0,
		label: '',
		status: true,
		siren: '',
		siret: '',
		nafApe: '',
		rcs: '',
		vatSubject: true,
		vatNumber: '',
		email: '',
		phone: '',
		createdAt: new Date(),
		types: [{ code: 'CLI', label: 'Client' }],
	});
	const [initialFormData, setInitialFormData] = useState(formData);

	// Hook de Navigation et paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const rolesParam = searchParams.get('roles') || '';
	const roles = rolesParam.split(',') as ('client' | 'fournisseur')[];
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// State des boutons toggle Client et Fournisseur
	const [isClientButton, setIsClientButton] = useState(
		roles.includes('client')
	);
	const [isFournisseurButton, setIsFournisseurButton] = useState(
		roles.includes('fournisseur')
	);

	// Une fois le composant monté et si un id est présent, fetch du tiers et préremplissage du formulaire
	React.useEffect(() => {
		if (id) {
			const token = localStorage.getItem('token');
			if (!token) return;

			getTiersById(Number(id), token)
				.then((data) => {
					setFormData(data);
					{
						data.types?.some((type) => type.code === 'CLI') &&
							setIsClientButton(true);
						data.types?.some((type) => type.code === 'FOU') &&
							setIsFournisseurButton(true);
					}
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// on prépare le tableau de role grace aux props
			const types = [];
			if (roles.includes('client')) {
				types.push({ code: 'CLI', label: 'Client' });
				setIsClientButton(true);
			}
			if (roles.includes('fournisseur')) {
				types.push({ code: 'FOU', label: 'Fournisseur' });
				setIsFournisseurButton(true);
			}
			// On met à jour que types dans le formData
			setFormData({
				...formData,
				types: types,
			});
			setLoading(false);
			setBaselineReady(true);
		}
		setInitialFormData(formData);
	}, [id]);

	// Gestion des changements dans le formulaire (inputs text et checkbox)
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type, checked } = e.target;

		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
			//Remplissage automatique de l'adresse électronique si le siren est renseigné
			...(name === 'siren' &&
			(!prev.electronicInvoicingAddress ||
				prev.electronicInvoicingAddress === prev.siren)
				? { electronicInvoicingAddress: value }
				: {}),
			//Remplissage automatique du siren et de l'adresse électronique si la TVA est renseignée
			...(name === 'vatNumber' &&
			!prev.siren &&
			!prev.electronicInvoicingAddress &&
			value.replace(/\s/g, '').length >= 13
				? {
						siren: value.replace(/\s/g, '').slice(-9),
						electronicInvoicingAddress: value.replace(/\s/g, '').slice(-9),
					}
				: {}),
		}));
	};

	// Gestion des changements dans le formulaire (toggle buttons)
	const handleToggleChange = (name: string, value: boolean) => {
		setFormData({ ...formData, [name]: value });
	};

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		// Si un id est présent, mise à jour du tiers
		if (id) {
			const onLeave = () => navigate('/app/tiers/' + id + '/manage');
			updateTiers(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
			// Sinon, on crée un nouveau Tiers et on redirige vers la liste des clients ou fournisseurs selon le cas
		} else {
			const onLeave = () => {
				if (isClientButton) {
					navigate('/app/customers');
				} else if (isFournisseurButton) {
					navigate('/app/suppliers');
				}
			};
			createTiers(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit:
							!id && created?.id
								? () =>
										navigate(`/app/tiers/${created.id}/edit`, {
											replace: true,
										})
								: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		handleSave(true);
	};

	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-4">
				{/* HEADER DE LA CARD */}
				<CardHeader
					icon={
						isClientButton && isFournisseurButton
							? faCity
							: isFournisseurButton
								? faIndustry
								: faBuilding
					}
					title={id ? "Édition d'un tiers" : 'Nouveau tiers'}
					isActive={formData.status ?? true}
					onToggle={(value) => setFormData({ ...formData, status: value })}
					createdAt={
						formData.createdAt
							? new Date(formData.createdAt).toISOString()
							: undefined
					}
					createdBy={
						formData.createdBy
							? formData.createdBy.firstName + ' ' + formData.createdBy.name
							: undefined
					}
					loading={loading}
				/>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-16">
							{/* COLONNE GAUCHE */}
							<div className="space-y-4">
								<InputField
									label="Label"
									name="label"
									value={formData.label}
									onChange={handleChange}
									required
									loading={loading}
								/>
								<InputField
									label="Email"
									name="email"
									value={formData.email}
									onChange={handleChange}
									loading={loading}
								/>
								<InputField
									label="Téléphone"
									name="phone"
									value={formData.phone}
									onChange={handleChange}
									loading={loading}
								/>
								<CheckboxField
									label="Client"
									checked={isClientButton}
									onChange={(value) => {
										setIsClientButton(value);
										const newTypes = (formData.types ?? []).filter(
											(type) => type.code !== 'CLI'
										);
										if (value) {
											newTypes.push({ code: 'CLI', label: 'Client' });
										}
										setFormData({ ...formData, types: newTypes });
									}}
									loading={loading}
								/>
								<CheckboxField
									label="Fournisseur"
									checked={isFournisseurButton}
									onChange={(value) => {
										setIsFournisseurButton(value);
										const newTypes = (formData.types ?? []).filter(
											(type) => type.code !== 'FOU'
										);
										if (value) {
											newTypes.push({ code: 'FOU', label: 'Fournisseur' });
										}
										setFormData({ ...formData, types: newTypes });
									}}
									loading={loading}
								/>
							</div>

							{/* COLONNE MILIEU */}
							<div className="space-y-4">
								<InputField
									label="Siren"
									name="siren"
									value={formData.siren}
									onChange={handleChange}
									loading={loading}
								/>
								<InputField
									label="Siret"
									name="siret"
									value={formData.siret}
									onChange={handleChange}
									loading={loading}
								/>
								<InputField
									label="Code Naf ou Ape"
									name="nafApe"
									value={formData.nafApe}
									onChange={handleChange}
									loading={loading}
								/>
								<InputField
									label="RCS"
									name="rcs"
									value={formData.rcs}
									onChange={handleChange}
									loading={loading}
								/>
							</div>

							{/* COLONNE DROITE */}
							<div className="space-y-4">
								<InputField
									label="Numéro de TVA"
									name="vatNumber"
									value={formData.vatNumber}
									onChange={handleChange}
									loading={loading}
								/>
								<CheckboxField
									label="Assujeti à la TVA"
									checked={formData.vatSubject ?? false}
									onChange={(value) => handleToggleChange('vatSubject', value)}
									loading={loading}
								/>

								<InputField
									label="Adresse électronique de facturation"
									name="electronicInvoicingAddress"
									value={formData.electronicInvoicingAddress ?? ''}
									onChange={handleChange}
									loading={loading}
								/>
							</div>
						</div>
					</form>

					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => {
							setFormData(initialFormData);
							if (id) navigate('/app/tiers/' + id + '/manage');
							else if (roles.includes('client')) navigate('/app/customers');
							else if (roles.includes('fournisseur'))
								navigate('/app/suppliers');
							else navigate('/app');
						}}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default TiersForm;
